/**
 * HerbKranti visitor attribution + analytics.
 *
 * - Captures UTMs + click IDs + referrer on first landing, stores `first_touch`
 *   in localStorage (persists across visits) and `last_touch` in sessionStorage
 *   (resets per browser session).
 * - Emits analytics events to the backend, which decides whether to forward to
 *   Telegram based on Admin toggles.
 * - Deduplicates client-side so React re-renders don't spam the network.
 */
import React, { createContext, useContext, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_FIRST = "hk_first_touch_v1";
const LS_SEEN = "hk_visitor_seen_v1";
const SS_SESSION = "hk_session_id_v1";
const SS_LAST = "hk_last_touch_v1";
const SS_RECENT = "hk_recent_events_v1";
const DEDUP_MS = 8000; // matches backend

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

const readParams = () => {
  if (typeof window === "undefined") return {};
  const sp = new URLSearchParams(window.location.search);
  const out = {};
  UTM_KEYS.forEach((k) => { const v = sp.get(k); if (v) out[k] = v; });
  const gclid = sp.get("gclid"); if (gclid) out.gclid = gclid;
  const fbclid = sp.get("fbclid"); if (fbclid) out.fbclid = fbclid;
  return out;
};

const classify = ({ utm_source, utm_medium, utm_campaign, gclid, fbclid, referrer }) => {
  if (utm_source) return { source: utm_source, medium: utm_medium || "utm", campaign: utm_campaign || "" };
  if (gclid) return { source: "Google", medium: "cpc", campaign: utm_campaign || "google-ads" };
  if (fbclid) return { source: "Facebook", medium: "cpc", campaign: utm_campaign || "meta-ads" };
  if (referrer) {
    try {
      const host = new URL(referrer).hostname.toLowerCase();
      const socials = {
        "instagram.com": ["Instagram", "Social"], "m.instagram.com": ["Instagram", "Social"],
        "facebook.com": ["Facebook", "Social"], "l.facebook.com": ["Facebook", "Social"],
        "m.facebook.com": ["Facebook", "Social"], "t.co": ["Twitter", "Social"],
        "twitter.com": ["Twitter", "Social"], "x.com": ["Twitter", "Social"],
        "linkedin.com": ["LinkedIn", "Social"], "youtube.com": ["YouTube", "Social"],
        "m.youtube.com": ["YouTube", "Social"], "youtu.be": ["YouTube", "Social"],
        "pinterest.com": ["Pinterest", "Social"], "reddit.com": ["Reddit", "Social"],
        "t.me": ["Telegram", "Social"], "wa.me": ["WhatsApp", "Referral"],
        "api.whatsapp.com": ["WhatsApp", "Referral"], "web.whatsapp.com": ["WhatsApp", "Referral"],
      };
      if (socials[host]) return { source: socials[host][0], medium: socials[host][1], campaign: "" };
      if (/(google|bing|duckduckgo|yahoo|yandex|ecosia|brave)\./.test(host)) {
        return { source: host.split(".").slice(-2, -1)[0]?.replace(/^./, (c) => c.toUpperCase()) || "Search", medium: "organic", campaign: "" };
      }
      return { source: host, medium: "referral", campaign: "" };
    } catch { return { source: "Direct", medium: "none", campaign: "" }; }
  }
  return { source: "Direct", medium: "none", campaign: "" };
};

const jsonRead = (storage, key) => {
  try { return JSON.parse(storage.getItem(key) || "null"); } catch { return null; }
};
const jsonWrite = (storage, key, v) => {
  try { storage.setItem(key, JSON.stringify(v)); } catch {}
};

const AnalyticsContext = createContext(null);

export const AnalyticsProvider = ({ children }) => {
  const location = useLocation();
  const initedRef = useRef(false);
  const sessionIdRef = useRef(null);

  const attribution = useMemo(() => {
    if (typeof window === "undefined") return { first_touch: null, last_touch: null, session_id: null };
    const params = readParams();
    const referrer = document.referrer || "";
    const currentTouch = { ...classify({ ...params, referrer }), referrer, ...params };

    // first touch (persistent)
    let first = jsonRead(localStorage, LS_FIRST);
    if (!first) {
      first = currentTouch;
      jsonWrite(localStorage, LS_FIRST, first);
    }
    // last touch (per session — refreshed on utm/referrer signals)
    let last = jsonRead(sessionStorage, SS_LAST);
    if (!last || params.utm_source || params.gclid || params.fbclid) {
      last = currentTouch;
      jsonWrite(sessionStorage, SS_LAST, last);
    }
    // session id
    let sid = sessionStorage.getItem(SS_SESSION);
    if (!sid) {
      sid = (crypto.randomUUID?.() || `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
      sessionStorage.setItem(SS_SESSION, sid);
    }
    sessionIdRef.current = sid;
    return { first_touch: first, last_touch: last, session_id: sid };
  }, []); // compute once per mount

  // Fire new/returning visitor exactly once per session
  useEffect(() => {
    if (initedRef.current) return;
    initedRef.current = true;
    const isReturning = !!localStorage.getItem(LS_SEEN);
    localStorage.setItem(LS_SEEN, "1");
    (async () => {
      try {
        await axios.post(`${API}/analytics/visitor`, {
          session_id: attribution.session_id,
          landing_page: location.pathname + (location.search || ""),
          referrer: document.referrer || "",
          first_touch: attribution.first_touch,
          last_touch: attribution.last_touch,
          is_returning: isReturning,
        });
      } catch { /* never break UX */ }
    })();
  }, [attribution, location.pathname, location.search]);

  const dedup = (key) => {
    const map = jsonRead(sessionStorage, SS_RECENT) || {};
    const now = Date.now();
    for (const k of Object.keys(map)) if (now - map[k] > 300000) delete map[k];
    if (map[key] && now - map[key] < DEDUP_MS) return true;
    map[key] = now;
    jsonWrite(sessionStorage, SS_RECENT, map);
    return false;
  };

  const fire = (event, data = {}, dedupBits = []) => {
    const key = `${event}::${(dedupBits || []).join("|")}`;
    if (dedupBits.length && dedup(key)) return;
    axios.post(`${API}/analytics/event`, {
      event,
      session_id: attribution.session_id,
      first_touch: attribution.first_touch,
      last_touch: attribution.last_touch,
      data,
    }).catch(() => {});
  };

  // Fire pageview on route change (dedup by path)
  const lastPathRef = useRef(null);
  useEffect(() => {
    const p = location.pathname + (location.search || "");
    if (lastPathRef.current === p) return;
    lastPathRef.current = p;
    fire("pageview", { path: p }, [p]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const value = useMemo(() => ({
    ...attribution,
    fireProductViewed: (product) => fire("product_viewed", {
      slug: product.slug, name: product.name, price: product.price,
    }, [product.slug]),
    fireAddToCart: (product, qty, cart_total) => fire("add_to_cart", {
      slug: product.slug, name: product.name, price: product.price, qty, cart_total,
    }, [product.slug, String(qty)]),
    fireCheckoutStarted: (items, total) => fire("checkout_started", {
      items: (items || []).map((it) => ({ slug: it.slug, name: it.name || it.product?.name, qty: it.qty })),
      total,
    }, [String(total), String((items || []).length)]),
    getSessionId: () => sessionIdRef.current,
  }), [attribution]); // eslint-disable-line react-hooks/exhaustive-deps

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};

export const useAnalytics = () => {
  const ctx = useContext(AnalyticsContext);
  return ctx || {
    session_id: null, first_touch: null, last_touch: null,
    fireProductViewed: () => {}, fireAddToCart: () => {},
    fireCheckoutStarted: () => {}, getSessionId: () => null,
  };
};
